import { useId, useLayoutEffect, useRef, type KeyboardEvent } from 'react'

/**
 * Поле ввода пакета 1С: подпись обязательна (диктор), Enter — «отправить»,
 * правка закрывается на время команды с неизвестным исходом. Отметка «изменено»
 * и «конфликт» — словом и значком рядом, не цветом рамки.
 */
export function InputField({
  label,
  value,
  onChange,
  onEnter,
  onBlur,
  disabled = false,
  inputMode,
  note,
  autoFocus,
  type = 'text',
  multiline = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  onEnter?: () => void
  onBlur?: () => void
  disabled?: boolean
  inputMode?: 'text' | 'numeric' | 'search'
  /** Пояснение под полем: «изменено», «в 1С сейчас: …». */
  note?: string | null
  autoFocus?: boolean
  type?: 'text' | 'search' | 'date'
  /**
   * Длинный текст (наименование): поле в несколько строк растёт вниз под текст, чтобы он читался целиком. Значение
   * остаётся одной строкой — перевод строки не вводится, Enter работает как у обычного поля.
   */
  multiline?: boolean
}) {
  const id = useId()
  const noteId = useId()
  const area = useRef<HTMLTextAreaElement>(null)
  // Высота — по тексту: при каждой правке и при повороте экрана (строки переносятся иначе).
  useLayoutEffect(() => {
    const element = area.current
    if (element === null) return undefined
    const fit = () => {
      element.style.height = 'auto'
      element.style.height = `${String(element.scrollHeight + element.offsetHeight - element.clientHeight)}px`
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [value, multiline])
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (multiline && event.key === 'Enter') {
      event.preventDefault()
      onEnter?.()
      return
    }
    if (event.key === 'Enter' && onEnter !== undefined) {
      event.preventDefault()
      onEnter()
    }
  }
  return (
    <div className="krp-field onec-field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
      </label>
      {multiline ? (
        <textarea
          ref={area}
          id={id}
          className="onec-field__multiline"
          rows={1}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-describedby={note ? noteId : undefined}
          onChange={(event) => onChange(event.target.value.replace(/\s*[\r\n]+\s*/g, ' '))}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
        />
      ) : (
      <input
        id={id}
        type={type}
        value={value}
        disabled={disabled}
        inputMode={inputMode}
        autoFocus={autoFocus}
        aria-describedby={note ? noteId : undefined}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
      />
      )}
      {note ? (
        <p id={noteId} className="onec-field__note">
          {note}
        </p>
      ) : null}
    </div>
  )
}
